/**
 * Single source of truth for `{{...}}` variables supported in notification templates.
 *
 * Consumed by:
 * - Backend `binding-resolver.ts` — truncates resolved values using `max`.
 * - Frontend notification editors — builds the @-menu, shows sample values
 *   for SMS/email length estimation and preview.
 *
 * `label_key` and `group_key` live under `PROFESSIONAL_APP_MARKETING_PAGE` in `fr-FR.ts`.
 */

import { NotificationType } from './translation_namespace/notification_namespace';

export interface NotificationBindingDefinition {
  /** Token as it appears in templates (e.g. `{{pro:name}}`). */
  token: string;
  /** `key` portion of the token, used as map key everywhere (e.g. `pro:name`). */
  key: string;
  /** Translation key under `PROFESSIONAL_APP_MARKETING_PAGE`. */
  label_key: string;
  /** Translation key under `PROFESSIONAL_APP_MARKETING_PAGE`. */
  group_key: string;
  /** Sample value used for SMS length estimation and UI preview. */
  sample: string;
  /** Max chars kept at send time (backend truncates the resolved value). */
  max: number;
  /** `true` when the binding is only resolved server-side (no frontend sample / label needed). */
  server_only?: boolean;
}

function binding(
  token: string,
  max: number,
  extras: Omit<NotificationBindingDefinition, 'token' | 'key' | 'max'>,
): NotificationBindingDefinition {
  return { token, key: token.slice(2, -2), max, ...extras };
}

export const NOTIFICATION_BINDINGS: NotificationBindingDefinition[] = [
  binding('{{pro:name}}', 40, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PRO_NAME',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PRO',
    sample: 'Coiffure du Grand Marché',
  }),
  binding('{{pro:phone}}', 20, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PRO_PHONE',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PRO',
    sample: '06 12 34 56 78',
  }),
  binding('{{pro:email}}', 50, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PRO_EMAIL',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PRO',
    sample: 'contact@monsalon.fr',
  }),
  binding('{{pro:address:full}}', 80, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PRO_ADDRESS',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PRO',
    sample: '12 rue des Lilas, 75011 Paris',
  }),
  binding('{{pro:address:city}}', 50, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{pro:logo}}', 500, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{customer:firstname}}', 30, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_CUSTOMER_FIRSTNAME',
    group_key: 'NOTIFICATIONS_VAR_GROUP_CUSTOMER',
    sample: 'Marie-Catherine',
  }),
  binding('{{customer:lastname}}', 30, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_CUSTOMER_LASTNAME',
    group_key: 'NOTIFICATIONS_VAR_GROUP_CUSTOMER',
    sample: 'Vandenberghe',
  }),
  binding('{{customer:email}}', 50, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:datetime}}', 40, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_DATETIME',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: 'lundi 14 avril 2026 à 14h30',
  }),
  binding('{{appointment:date}}', 30, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_DATE',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: '14 avril 2026',
  }),
  binding('{{appointment:prestations}}', 60, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_PRESTATIONS',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: 'Coupe + Couleur + Soin',
  }),
  binding('{{appointment:location}}', 60, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_LOCATION',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: '12 rue des Lilas, 75011 Paris',
  }),
  binding('{{appointment:meeting_link}}', 2000, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_MEETING_LINK',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: 'https://meet.example.com/abc-defg-hij',
  }),
  binding('{{appointment:phone_number}}', 30, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_PHONE_NUMBER',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: '+33 1 23 45 67 89',
  }),
  // Bindings "block" : émettent un fragment HTML complet (label + lien) si la
  // donnée est présente, sinon une chaîne vide. Utilisés par les templates par
  // défaut (booking_confirmed / booking_reminder) pour ne pas afficher de lignes
  // vides sur les RDV non-visio/non-téléphone. `server_only: true` → invisibles
  // dans le builder côté pro, le rendu HTML est figé.
  binding('{{appointment:meeting_link_html}}', 2200, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:phone_number_html}}', 200, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:instructions_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Récap HTML des prestations (nom + quantité + suppléments + prix + total),
  // pré-rendu côté serveur (prix historisés). `server_only` → invisible dans le
  // builder côté pro, le rendu HTML est figé. Cf. binding-resolver.ts +
  // notification-bridge.service.ts (renderPrestationsRecapHtml).
  binding('{{appointment:prestations_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Éléments du récap de réservation (spec Parcours § 7.9, planche 10), assemblés par les modèles par
  // défaut et insérables un à un par le pro dans les emails client (NOTIFICATION_EMAIL_BINDABLE_FIELDS).
  // Les `_html` rendent une ligne complète (libellé + valeur formatée serveur) si la donnée existe, sinon
  // une chaîne vide. Cf. binding-resolver.ts + notification-bridge.service.ts.
  binding('{{appointment:team_member}}', 200, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:team_member_html}}', 400, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:prestations_detail_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:discount_html}}', 2000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:total_html}}', 600, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:voucher_html}}', 600, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:remaining_amount}}', 40, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:remaining_amount_html}}', 400, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:cancellation_policy_html}}', 800, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:manage_link_html}}', 800, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Ancienne date d'un RDV reprogrammé (template booking_updated) : résolus à
  // l'envoi depuis `appointment.previous_start` (cf. binding-resolver.ts).
  binding('{{appointment:previous_datetime}}', 60, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:previous_datetime_html}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Bindings « diff » de l'email de modification (BOOKING_UPDATED), pré-rendus côté serveur :
  // date/lieu avec l'ancien barré s'il a changé, et diff prestations/suppléments (retirés barrés,
  // ajoutés soulignés). Cf. binding-resolver.ts + notification-bridge.service.ts.
  binding('{{appointment:datetime_change_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:location_change_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:prestations_changes_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Encadré « en attente de validation » (pro en `confirm_every_booking`, résa WAITING_CONFIRMATION) :
  // version client (BOOKING_UPDATED) et version pro (PRO_BOOKING_RESCHEDULED). Vides sinon.
  binding('{{appointment:awaiting_confirmation_html}}', 1000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:awaiting_confirmation_pro_html}}', 1000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{appointment:manage_url}}', 300, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_APPOINTMENT_MANAGE_URL',
    group_key: 'NOTIFICATIONS_VAR_GROUP_APPOINTMENT',
    sample: 'https://bs.ms/XXXXXX',
  }),
  // Page publique de réservation du pro (`/book/<slug>`) — cible du bouton
  // « Reprendre un rendez-vous » des templates d'annulation par défaut.
  // `server_only` : pas exposé dans l'éditeur côté pro.
  binding('{{appointment:booking_url}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Fiche du RDV dans l'app pro (`/agenda/<id>/view`) — CTA « Voir dans mon agenda »
  // des emails pro (nouveau RDV / RDV annulé).
  binding('{{appointment:agenda_url}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Page abonnement/quotas de l'app pro — CTA de l'email PRO_QUOTA_LOW.
  binding('{{pro:subscription_url}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Bouton « Réserver » vers le widget public (VOUCHER_EXPIRING) — bloc HTML complet,
  // vide si le pro n'a pas de slug (pas de bouton mort).
  binding('{{voucher:booking_button_html}}', 600, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{review:url}}', 300, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_REVIEW_URL',
    group_key: 'NOTIFICATIONS_VAR_GROUP_REVIEW',
    sample: 'https://bs.ms/XXXXXX',
  }),
  // Montant d'un paiement (encaissé, remboursé ou demandé), ou somme encaissée sur la réservation
  // (emails de réservation), pré-formaté côté serveur.
  binding('{{payment:amount}}', 15, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PAYMENT_AMOUNT',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PAYMENT',
    sample: '45,00 €',
  }),
  // Page de paiement sécurisée d'une demande du pro (PAYMENT_REQUEST). URL longue (uuid de 64 caractères) :
  // le SMS la raccourcit à l'envoi, d'où un échantillon court pour l'estimation de longueur.
  binding('{{payment:url}}', 300, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_PAYMENT_URL',
    group_key: 'NOTIFICATIONS_VAR_GROUP_PAYMENT',
    sample: 'https://bs.ms/XXXXXX',
  }),
  // Phrase de la demande (PAYMENT_REQUEST), texte brut rédigé côté serveur selon le type : paiement, empreinte
  // bancaire ou carte enregistrée en garantie (aucun débit immédiat dans ces deux derniers cas).
  binding('{{payment:request_summary}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Détails de la demande (PAYMENT_REQUEST) : objet, rendez-vous et description du pro, chaque ligne seulement si
  // renseignée. Le résolveur échappe les textes saisis par le pro.
  binding('{{payment:details_html}}', 5000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Prestations de la demande (PAYMENT_REQUEST), mêmes lignes que la page de paiement + total. Absent sans prestation.
  binding('{{payment:prestations_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Ligne « Payé : … » du récap de réservation, vide si rien n'a été encaissé.
  binding('{{payment:amount_html}}', 300, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Consigne jointe au « Paiement reçu » quand un lien du pro aboutit sur un RDV annulé (texte brut pré-rendu
  // côté serveur, séparateur « · » inclus, vide dans le cas nominal). Le max couvre la plus longue consigne
  // (~115 caractères), sinon capValue() la tronquerait.
  binding('{{payment:cancelled_reservation_warning}}', 200, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  // Format complet `XXXXX-XXXXX-XXXXX-XXXXX-XXXXX` = 29 caractères (5 groupes de 5 + 4 tirets).
  // Le max doit couvrir toute la longueur, sinon capValue() tronque le code avec « … ».
  binding('{{voucher:code}}', 29, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_VOUCHER_CODE',
    group_key: 'NOTIFICATIONS_VAR_GROUP_VOUCHER',
    sample: 'ABCDE-12345-FGHIJ-67890-KLMNO',
  }),
  binding('{{voucher:amount}}', 15, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_VOUCHER_AMOUNT',
    group_key: 'NOTIFICATIONS_VAR_GROUP_VOUCHER',
    sample: '50,00 €',
  }),
  // Montant qui vient d'être débité (utilisation) ou crédité (recharge) — bons AMOUNT uniquement.
  binding('{{voucher:used_amount}}', 15, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_VOUCHER_USED_AMOUNT',
    group_key: 'NOTIFICATIONS_VAR_GROUP_VOUCHER',
    sample: '20,00 €',
  }),
  // Solde restant après l'opération — bons AMOUNT uniquement.
  binding('{{voucher:remaining_amount}}', 15, {
    label_key: 'NOTIFICATIONS_VAR_LABEL_VOUCHER_REMAINING_AMOUNT',
    group_key: 'NOTIFICATIONS_VAR_GROUP_VOUCHER',
    sample: '30,00 €',
  }),
  // Récap HTML de l'opération (débité/rechargé + solde restant), pré-rendu côté serveur et adapté au
  // type de bon (AMOUNT vs PACK/PRESTATION). `server_only` → rendu figé, invisible dans le builder.
  binding('{{voucher:change_summary_html}}', 20000, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
  binding('{{voucher:expiration}}', 30, {
    label_key: '',
    group_key: '',
    sample: '',
    server_only: true,
  }),
];

export const NOTIFICATION_BINDING_MAX_BY_KEY: Record<string, number> = Object.fromEntries(
  NOTIFICATION_BINDINGS.map((b): [string, number] => [b.key, b.max]),
);

export const NOTIFICATION_BINDING_SAMPLE_BY_KEY: Record<string, string> = Object.fromEntries(
  NOTIFICATION_BINDINGS.filter((b): boolean => !b.server_only).map((b): [string, string] => [b.key, b.sample]),
);

export const NOTIFICATION_BINDING_LABEL_KEY_BY_KEY: Record<string, string> = Object.fromEntries(
  NOTIFICATION_BINDINGS.filter((b): boolean => !b.server_only).map((b): [string, string] => [b.key, b.label_key]),
);

export const NOTIFICATION_BINDINGS_USER_FACING: NotificationBindingDefinition[] = NOTIFICATION_BINDINGS.filter(
  (b): boolean => !b.server_only,
);

/**
 * Échantillons HTML pour les block bindings `_html` (server_only) — utilisés
 * UNIQUEMENT par la preview du builder côté pro. Au rendu réel (envoi), ces
 * tokens sont résolus par `binding-resolver.ts` (resolveKey). Sans cette map,
 * la preview affichait les tokens bruts `{{appointment:..._html}}` dans le
 * composant texte (qui rend en HTML). Le format reproduit la sortie de
 * `resolveKey()` pour rester cohérent avec l'email réellement envoyé.
 */
const PREVIEW_CONDITIONAL_NOTE_STYLE =
  'margin:6px 0 0 0;font-size:11px;font-style:italic;color:#94a3b8;display:flex;align-items:center;gap:4px;';
const PREVIEW_CONDITIONAL_BOX_STYLE =
  'margin:8px 0;padding:10px 12px;border:1px dashed #cbd5e1;border-radius:8px;background:transparent;';

/**
 * Encadre un fragment de preview avec un liseré pointillé builder-only et une
 * note « affiché uniquement si renseigné », pour signaler au pro qu'une ligne
 * est conditionnelle (rendue à l'envoi seulement si la donnée existe). Cet
 * habillage n'apparaît jamais dans l'email réel — il vit uniquement dans la map
 * d'échantillons de preview, pas dans `binding-resolver.ts`.
 */
function conditionalPreviewBlock(inner: string, note: string): string {
  return (
    `<div style="${PREVIEW_CONDITIONAL_BOX_STYLE}">${inner}` +
    `<p style="${PREVIEW_CONDITIONAL_NOTE_STYLE}">👁 ${note}</p></div>`
  );
}

// Cas le plus courant (paiement complet) ; l'envoi réel adapte la phrase à l'empreinte et à la carte enregistrée.
const PAYMENT_REQUEST_SUMMARY_PREVIEW =
  'Coiffure du Grand Marché vous invite à régler 45,00 € en ligne, sur une page de paiement sécurisée.';

/**
 * Échantillons **texte brut** des mêmes block bindings, pour les surfaces qui
 * affichent un aperçu sans rendu HTML (carte récap de la modale notification :
 * `body_text_preview` est déjà détaggé côté backend). Sans ça, les fragments
 * HTML de `NOTIFICATION_BLOCK_PREVIEW_HTML_BY_KEY` s'affichaient en balises brutes.
 */
export const NOTIFICATION_BLOCK_PREVIEW_TEXT_BY_KEY: Record<string, string> = {
  'appointment:prestations_html':
    'Prestations : Coupe femme × 2 : 80,00 € (+ Shampoing premium 12,00 €) ; Couleur : 45,00 €. Total : 137,00 € (réduction code promo et bon utilisé ajoutés si appliqués)',
  'appointment:meeting_link_html': 'Lien visio : https://meet.example.com/abc-defg-hij (si renseigné)',
  'appointment:phone_number_html': 'Téléphone : +33 1 23 45 67 89 (si renseigné)',
  'appointment:instructions_html': "Instructions : Merci d'arriver 5 minutes en avance. (si renseignées)",
  'appointment:previous_datetime_html': 'Ancienne date : jeudi 12 juin 2026 à 10:00 (si reprogrammé)',
  'appointment:datetime_change_html': 'Date : jeudi 12 juin 2026 à 10:00 (barré) → samedi 14 juin 2026 à 16:00',
  'appointment:location_change_html': 'Lieu : Salon Centre (barré) → Salon Nord (si le lieu a changé)',
  'appointment:prestations_changes_html':
    'Prestations : Coupe femme × 1 : 40,00 € ; Couleur : 45,00 € (ajoutée, soulignée) ; Brushing : 25,00 € (retiré, barré). Total : 85,00 €',
  'appointment:awaiting_confirmation_html':
    'Ce rendez-vous doit encore être confirmé par Coiffure du Grand Marché. (si vous validez chaque réservation)',
  'appointment:awaiting_confirmation_pro_html':
    'Ce déplacement attend votre validation. (si vous validez chaque réservation)',
  'voucher:change_summary_html': 'Montant utilisé : 20,00 €. Solde restant : 30,00 €.',
  // Conditionnelle (RDV annulé seulement) : absente de l'aperçu du cas nominal, jamais le token brut.
  'payment:cancelled_reservation_warning': '',
  // Récap de réservation (planche 10) : mêmes valeurs d'exemple que l'aperçu HTML ci-dessous.
  'appointment:team_member_html': 'Avec Sophie (si un membre est réservé)',
  'appointment:prestations_detail_html': 'Massage signature · 1h : 60,00 € ; + Supplément pierres chaudes : 10,00 €',
  'appointment:discount_html': 'Remise Black Week : -12,00 € (si une remise s’applique)',
  'appointment:total_html': 'Total : 70,00 € (barré) 58,00 €',
  'appointment:voucher_html': 'Réduction du bon : -20,00 € (si un bon est utilisé)',
  'payment:amount_html': 'Payé : 17,40 € (si un paiement a été reçu)',
  'appointment:remaining_amount_html': 'Reste sur place : 40,60 € (s’il reste un montant à régler)',
  'appointment:cancellation_policy_html':
    'Annulation gratuite jusqu’au 28 octobre 2026 à 14:00, puis l’acompte de 17,40 € est conservé. (si une date limite est fixée)',
  'appointment:manage_link_html': 'Gérer mon rendez-vous (lien)',
  'payment:request_summary': PAYMENT_REQUEST_SUMMARY_PREVIEW,
  'payment:details_html':
    'Objet : Acompte soin visage. Rendez-vous : samedi 14 juin 2026 à 16:00. Description : À régler avant votre venue. (si renseignés)',
  'payment:prestations_html':
    'Prestations : Soin visage éclat : 40,00 € (+ Massage des mains 5,00 €). Total : 45,00 € (si la demande en contient)',
};

/**
 * Aperçu builder des éléments du récap de réservation : sortie de `binding-resolver.ts` pour le
 * rendez-vous de la planche 10 (acompte de 30 %, promotion de 12 €). Verrouillé par
 * `binding-resolver.booking-recap.spec.ts` (aperçu = rendu réel, hors encadré conditionnel).
 */
export const BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY: Record<string, string> = {
  'appointment:team_member_html': '<p style="margin:0 0 4px 0;">Avec Sophie</p>',
  'appointment:prestations_detail_html':
    '<div style="margin:0 0 8px 0;"><span><strong>Massage signature</strong> · 1h : 60,00 €</span>' +
    '<div style="margin:2px 0 0 12px;font-size:13px;color:#6b7280;">+ Supplément pierres chaudes : 10,00 €</div></div>',
  'appointment:discount_html': '<p style="margin:0 0 4px 0;">Remise Black Week : -12,00 €</p>',
  'appointment:total_html':
    '<p style="margin:8px 0 4px 0;"><strong>Total : <span style="text-decoration: line-through; color: #6b7280;">70,00 €</span> 58,00 €</strong></p>',
  'appointment:voucher_html': '<p style="margin:0 0 4px 0;">Réduction du bon : -20,00 €</p>',
  'payment:amount_html': '<p style="margin:0 0 4px 0;">Payé : 17,40 €</p>',
  'appointment:remaining_amount_html': '<p style="margin:0 0 4px 0;"><strong>Reste sur place : 40,60 €</strong></p>',
  'appointment:cancellation_policy_html':
    '<p style="margin:8px 0 0 0;font-size:13px;color:#6b7280;">Annulation gratuite jusqu&#39;au 28 octobre 2026 à 14:00, puis l&#39;acompte de 17,40 € est conservé.</p>',
  'appointment:manage_link_html':
    '<p style="margin:12px 0 0 0;"><a href="https://bs.ms/XXXXXX">Gérer mon rendez-vous</a></p>',
};

export const NOTIFICATION_BLOCK_PREVIEW_HTML_BY_KEY: Record<string, string> = {
  // Non conditionnel (toujours rendu) → pas de liseré « si renseigné ». Le markup
  // reproduit la sortie de renderPrestationsRecapHtml (notification-bridge.service.ts)
  // pour que la preview builder ≈ l'email réel.
  'appointment:prestations_html':
    '<p style="margin:0 0 6px 0;"><strong>Prestations :</strong></p>' +
    '<div style="margin:0 0 8px 0;"><span><strong>Coupe femme</strong> × 2 : 80,00 €</span>' +
    '<div style="margin:2px 0 0 12px;font-size:13px;color:#6b7280;">+ Shampoing premium : 12,00 €</div></div>' +
    '<div style="margin:0 0 8px 0;"><span><strong>Couleur</strong> : 45,00 €</span></div>' +
    '<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;"><strong>Total : 137,00 €</strong></div>' +
    conditionalPreviewBlock(
      '<div style="margin:4px 0 0 0;">Réduction code promo (ETE20) : -27,40 € — avant le Total, alors affiché après remise</div>' +
        '<div style="margin:4px 0 0 0;">Réduction du bon : -20,00 € — après le Total</div>',
      'Affiché uniquement si un code promo ou un bon a été appliqué',
    ),
  'appointment:meeting_link_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>Lien visio :</strong> <a href="https://meet.example.com/abc-defg-hij">https://meet.example.com/abc-defg-hij</a></p>',
    'Affiché uniquement si un lien visio est renseigné',
  ),
  'appointment:phone_number_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>Téléphone :</strong> <a href="tel:+33123456789">+33 1 23 45 67 89</a></p>',
    'Affiché uniquement si un numéro de téléphone est renseigné',
  ),
  'appointment:instructions_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>À savoir avant votre venue :</strong></p><p style="margin:4px 0 0 0;">Merci d\'arriver 5 minutes en avance.</p>',
    'Affiché uniquement si des instructions correspondent à la réservation (prestations, lieu)',
  ),
  'appointment:previous_datetime_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>Ancienne date :</strong> jeudi 12 juin 2026 à 10:00</p>',
    "Affichée uniquement lors d'une reprogrammation",
  ),
  // Vue « suivi des modifications » (BOOKING_UPDATED) : date/lieu actuels avec l'ancien barré s'il a
  // changé ; diff prestations (ajouté souligné, retiré barré). Markup ≈ binding-resolver.ts.
  'appointment:datetime_change_html':
    '<p style="margin:0;"><strong>Date :</strong> <span style="text-decoration: line-through; color: #6b7280;">jeudi 12 juin 2026 à 10:00</span> → samedi 14 juin 2026 à 16:00</p>',
  'appointment:location_change_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>Lieu :</strong> <span style="text-decoration: line-through; color: #6b7280;">Salon Centre - 1 rue X</span> → Salon Nord - 9 av. Y</p>',
    'Ancien lieu barré uniquement si le lieu a changé',
  ),
  'appointment:prestations_changes_html':
    '<p style="margin:0 0 6px 0;"><strong>Prestations :</strong></p>' +
    '<div style="margin:0 0 8px 0;"><span><strong>Coupe femme</strong> : 40,00 €</span></div>' +
    '<div style="margin:0 0 8px 0;"><span style="text-decoration: underline;"><strong>Couleur</strong> : 45,00 €</span></div>' +
    '<div style="margin:0 0 8px 0;"><span style="text-decoration: line-through; color: #6b7280;"><strong>Brushing</strong> : 25,00 €</span></div>' +
    '<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;"><strong>Total : 85,00 €</strong></div>',
  'appointment:awaiting_confirmation_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>⏳ Ce rendez-vous doit encore être confirmé par Coiffure du Grand Marché. Vous recevrez un email dès sa validation.</strong></p>',
    'Affiché uniquement si vous validez chaque réservation',
  ),
  'appointment:awaiting_confirmation_pro_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>⏳ Ce déplacement attend votre validation depuis votre agenda.</strong></p>',
    'Affiché uniquement si vous validez chaque réservation',
  ),
  // Récap d'opération sur un bon (utilisation / recharge) — markup ≈ VoucherNotificationService.
  'voucher:change_summary_html':
    '<p style="margin:0;"><strong>Montant utilisé :</strong> 20,00 €</p>' +
    '<p style="margin:4px 0 0 0;"><strong>Solde restant :</strong> 30,00 €</p>',
  'payment:cancelled_reservation_warning': '',
  // Récap de réservation (planche 10). Prestations, total et lien toujours rendus ; les autres lignes
  // seulement si la donnée existe.
  'appointment:team_member_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:team_member_html'],
    'Affiché uniquement si un membre est réservé',
  ),
  'appointment:prestations_detail_html': BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:prestations_detail_html'],
  'appointment:discount_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:discount_html'],
    'Affiché uniquement si une remise s’applique',
  ),
  'appointment:total_html': BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:total_html'],
  'appointment:voucher_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:voucher_html'],
    'Affiché uniquement si une carte cadeau ou un bon est utilisé',
  ),
  'payment:amount_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['payment:amount_html'],
    'Affiché uniquement si un paiement a été reçu',
  ),
  'appointment:remaining_amount_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:remaining_amount_html'],
    'Affiché uniquement s’il reste un montant à régler',
  ),
  'appointment:cancellation_policy_html': conditionalPreviewBlock(
    BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:cancellation_policy_html'],
    'Affiché uniquement si une date limite d’annulation est fixée',
  ),
  'appointment:manage_link_html': BOOKING_RECAP_PREVIEW_INNER_HTML_BY_KEY['appointment:manage_link_html'],
  'payment:request_summary': PAYMENT_REQUEST_SUMMARY_PREVIEW,
  // Markup ≈ binding-resolver.ts (paymentRequestDetailsHtml).
  'payment:details_html': conditionalPreviewBlock(
    '<p style="margin:0;"><strong>Objet :</strong> Acompte soin visage</p>' +
      '<p style="margin:4px 0 0 0;"><strong>Rendez-vous :</strong> samedi 14 juin 2026 à 16:00</p>' +
      '<p style="margin:4px 0 0 0;"><strong>Description :</strong> À régler avant votre venue.</p>',
    'Chaque ligne est affichée uniquement si elle est renseignée',
  ),
  // Markup ≈ binding-resolver.ts (paymentRequestPrestationsHtml).
  'payment:prestations_html': conditionalPreviewBlock(
    '<p style="margin:0 0 6px 0;"><strong>Prestations :</strong></p>' +
      '<div style="margin:0 0 8px 0;"><span><strong>Soin visage éclat</strong> : 40,00 €</span>' +
      '<div style="margin:2px 0 0 12px;font-size:13px;color:#6b7280;">+ Massage des mains (5,00 €)</div></div>' +
      '<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;"><strong>Total : 45,00 €</strong></div>',
    'Affiché uniquement si la demande contient des prestations',
  ),
};

/**
 * Bindable fields exposed to the email builder when used in a notification context.
 * Same shape as `EMAIL_BINDABLE_FIELDS` (campaigns) so it can be plugged into
 * `BuilderTypeContext`, `BindingSelector` and `ComponentInspector` without changes.
 *
 * Translation keys live under `DYNAMIC_FORM` namespace.
 */
export const NOTIFICATION_EMAIL_BINDABLE_FIELDS: Record<
  string,
  { label: string; group: string; type: 'string' | 'image' }
> = {
  'pro:name': { label: 'BINDING_PRO_NAME', group: 'BINDING_GROUP_GENERAL', type: 'string' },
  'pro:logo': { label: 'BINDING_PRO_LOGO', group: 'BINDING_GROUP_MEDIA', type: 'image' },
  'pro:phone': { label: 'BINDING_PRO_PHONE', group: 'BINDING_GROUP_CONTACT', type: 'string' },
  'pro:email': { label: 'BINDING_PRO_EMAIL', group: 'BINDING_GROUP_CONTACT', type: 'string' },
  'pro:address:full': { label: 'BINDING_PRO_FULL_ADDRESS', group: 'BINDING_GROUP_ADDRESS', type: 'string' },
  'customer:firstname': { label: 'BINDING_CUSTOMER_FIRST_NAME', group: 'BINDING_GROUP_CUSTOMER', type: 'string' },
  'customer:lastname': { label: 'BINDING_CUSTOMER_LAST_NAME', group: 'BINDING_GROUP_CUSTOMER', type: 'string' },
  'appointment:datetime': {
    label: 'BINDING_APPOINTMENT_DATETIME',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:date': { label: 'BINDING_APPOINTMENT_DATE', group: 'BINDING_GROUP_APPOINTMENT', type: 'string' },
  'appointment:prestations': {
    label: 'BINDING_APPOINTMENT_PRESTATIONS',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:location': {
    label: 'BINDING_APPOINTMENT_LOCATION',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:meeting_link': {
    label: 'BINDING_APPOINTMENT_MEETING_LINK',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:phone_number': {
    label: 'BINDING_APPOINTMENT_PHONE_NUMBER',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  // Block bindings conditionnels : émettent une ligne complète (label + valeur)
  // uniquement si la donnée existe à l'envoi. Exposés ici pour que l'éditeur
  // rich-text affiche un label sur le chip (au lieu de la clé brute) ; le label
  // « (si renseigné) » communique la nature conditionnelle.
  'appointment:meeting_link_html': {
    label: 'BINDING_APPOINTMENT_MEETING_LINK_BLOCK',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:phone_number_html': {
    label: 'BINDING_APPOINTMENT_PHONE_NUMBER_BLOCK',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:instructions_html': {
    label: 'BINDING_APPOINTMENT_INSTRUCTIONS_BLOCK',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:previous_datetime_html': {
    label: 'BINDING_APPOINTMENT_PREVIOUS_DATETIME_BLOCK',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  'appointment:manage_url': {
    label: 'BINDING_APPOINTMENT_MANAGE_URL',
    group: 'BINDING_GROUP_APPOINTMENT',
    type: 'string',
  },
  // Récap de réservation (planche 10) : réservé aux emails client qui le montrent
  // (cf. getNotificationEmailBindableFields).
  'appointment:team_member_html': {
    label: 'BINDING_APPOINTMENT_TEAM_MEMBER_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:prestations_detail_html': {
    label: 'BINDING_APPOINTMENT_PRESTATIONS_DETAIL_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:discount_html': {
    label: 'BINDING_APPOINTMENT_DISCOUNT_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:total_html': {
    label: 'BINDING_APPOINTMENT_TOTAL_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:voucher_html': {
    label: 'BINDING_APPOINTMENT_VOUCHER_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'payment:amount_html': {
    label: 'BINDING_PAYMENT_AMOUNT_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:remaining_amount_html': {
    label: 'BINDING_APPOINTMENT_REMAINING_AMOUNT_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:cancellation_policy_html': {
    label: 'BINDING_APPOINTMENT_CANCELLATION_POLICY_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'appointment:manage_link_html': {
    label: 'BINDING_APPOINTMENT_MANAGE_LINK_BLOCK',
    group: 'BINDING_GROUP_BOOKING_RECAP',
    type: 'string',
  },
  'review:url': { label: 'BINDING_REVIEW_URL', group: 'BINDING_GROUP_REVIEW', type: 'string' },
  'payment:amount': { label: 'BINDING_PAYMENT_AMOUNT', group: 'BINDING_GROUP_PAYMENT', type: 'string' },
  'payment:url': { label: 'BINDING_PAYMENT_URL', group: 'BINDING_GROUP_PAYMENT', type: 'string' },
  'payment:request_summary': {
    label: 'BINDING_PAYMENT_REQUEST_SUMMARY',
    group: 'BINDING_GROUP_PAYMENT',
    type: 'string',
  },
  'payment:details_html': { label: 'BINDING_PAYMENT_DETAILS_BLOCK', group: 'BINDING_GROUP_PAYMENT', type: 'string' },
  'payment:prestations_html': {
    label: 'BINDING_PAYMENT_PRESTATIONS_BLOCK',
    group: 'BINDING_GROUP_PAYMENT',
    type: 'string',
  },
  'voucher:code': { label: 'BINDING_VOUCHER_CODE', group: 'BINDING_GROUP_VOUCHER', type: 'string' },
  'voucher:amount': { label: 'BINDING_VOUCHER_AMOUNT', group: 'BINDING_GROUP_VOUCHER', type: 'string' },
  'voucher:used_amount': { label: 'BINDING_VOUCHER_USED_AMOUNT', group: 'BINDING_GROUP_VOUCHER', type: 'string' },
  'voucher:remaining_amount': {
    label: 'BINDING_VOUCHER_REMAINING_AMOUNT',
    group: 'BINDING_GROUP_VOUCHER',
    type: 'string',
  },
  'voucher:expiration': {
    label: 'BINDING_VOUCHER_EXPIRATION',
    group: 'BINDING_GROUP_VOUCHER',
    type: 'string',
  },
};

/**
 * Emails client qui montrent le récap d'une réservation (spec Parcours § 7.9) : confirmation, demande en
 * attente, modification et rappel. Les annulations et le remboursement restent sans récap.
 */
export const NOTIFICATION_BOOKING_RECAP_EVENT_TYPES: readonly NotificationType[] = [
  NotificationType.BOOKING_CONFIRMED,
  NotificationType.BOOKING_WAITING_CONFIRMATION,
  NotificationType.BOOKING_UPDATED,
  NotificationType.BOOKING_REMINDER,
];

/**
 * Éléments proposés dans l'éditeur de l'email d'un événement : ceux du récap de réservation
 * (`BINDING_GROUP_BOOKING_RECAP`) n'apparaissent que pour les emails qui le montrent.
 */
export function getNotificationEmailBindableFields(eventType: string): typeof NOTIFICATION_EMAIL_BINDABLE_FIELDS {
  if ((NOTIFICATION_BOOKING_RECAP_EVENT_TYPES as readonly string[]).includes(eventType)) {
    return NOTIFICATION_EMAIL_BINDABLE_FIELDS;
  }
  return Object.fromEntries(
    Object.entries(NOTIFICATION_EMAIL_BINDABLE_FIELDS).filter(
      ([, field]): boolean => field.group !== 'BINDING_GROUP_BOOKING_RECAP',
    ),
  );
}
